import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";

interface Product {
  id: string;
  slug: string;
  icon: string | null;
  image_url: string | null;
  sort_order: number;
  is_active: boolean;
}

interface ProductTranslation {
  id: string;
  product_id: string;
  language_code: string;
  name: string;
  description: string | null;
  features: string[] | null;
}

export interface ProductWithTranslation extends Product {
  name: string;
  description: string | null;
  features: string[] | null;
}

export const useProducts = () => {
  const { currentLanguage } = useLanguage();

  return useQuery({
    queryKey: ["products", currentLanguage],
    queryFn: async () => {
      // Fetch products
      const { data: products, error: productsError } = await supabase
        .from("products")
        .select("*")
        .eq("is_active", true)
        .order("sort_order");

      if (productsError) throw productsError;

      if (!products || products.length === 0) {
        return [] as ProductWithTranslation[];
      }

      // Fetch translations for current language
      const productIds = products.map((p) => p.id);
      const { data: translations, error: translationsError } = await supabase
        .from("product_translations")
        .select("*")
        .eq("language_code", currentLanguage)
        .in("product_id", productIds);

      if (translationsError) throw translationsError;

      // Merge products with translations
      const translationMap = new Map<string, ProductTranslation>();
      translations?.forEach((t) => {
        translationMap.set(t.product_id, t as ProductTranslation);
      });

      return (products as Product[]).map((product) => {
        const translation = translationMap.get(product.id);
        return {
          ...product,
          name: translation?.name || product.slug,
          description: translation?.description || null,
          features: translation?.features || null,
        } as ProductWithTranslation;
      });
    },
  });
};

export const useAllProducts = () => {
  return useQuery({
    queryKey: ["products-all"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products")
        .select(`
          *,
          product_translations (*)
        `)
        .order("sort_order");

      if (error) throw error;
      return data;
    },
  });
};
